import { getUserGuests, Guest } from './guestsService';
import { getRecipeById, Recipe } from './recipesService';
import { createNotification } from './notificationsService';
import { getEvent } from './eventsService';
import allergiesData from '../data/alergies.json';
import { parseAllergyTokens } from '../utils/allergyTokens';

// Tipo para o banco de dados de alergias
type AllergiesDatabase = Record<string, string[]>;
const typedAllergiesData: AllergiesDatabase = allergiesData;

export interface AllergyConflict {
  guestId: string;
  guestName: string;
  allergens: string[];
  conflictingIngredients: string[];
}

/**
 * Normaliza string para comparação (minúsculas, sem acentos, trim)
 */
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

/**
 * Encontra alérgenos ocultos em um ingrediente baseado no banco de dados JSON
 */
const findHiddenAllergens = (ingredientName: string): string[] => {
  const normalizedIngredient = normalizeText(ingredientName);

  // Busca direta
  for (const [key, allergens] of Object.entries(typedAllergiesData)) {
    if (normalizeText(key) === normalizedIngredient) {
      return allergens;
    }
  }

  // Busca parcial (ex: "Bolo de chocolate com cobertura" contendo "Bolo de chocolate")
  // Isso pode gerar falsos positivos, então vamos ser conservadores e usar apenas match direto ou invertido muito forte
  // Por enquanto, vamos manter a busca direta e talvez uma busca de "contém" do banco no ingrediente

  const foundAllergens: Set<string> = new Set();

  for (const [key, allergens] of Object.entries(typedAllergiesData)) {
    const normalizedKey = normalizeText(key);
    // Se o ingrediente da receita contém o item do banco (ex: ingrediente "Fatia de Bolo de Chocolate" contém "Bolo de chocolate")
    if (normalizedIngredient.includes(normalizedKey) && normalizedKey.length > 3) { // >3 para evitar matches curtos demais
      allergens.forEach(a => foundAllergens.add(a));
    }
  }

  return Array.from(foundAllergens);
};

/**
 * Verifica se algum ingrediente da receita pode causar alergia nos convidados
 * Suporta verificar por IDs (existente) ou Objetos (para dialogs em tempo real)
 */
export const checkRecipeAllergies = async (
  userId: string,
  recipeOrId: string | Recipe,
  eventOrId: string | { guests?: string[] } | Guest[], // Pode ser ID do evento, Objeto parcial do evento, ou lista de Guests direto
  guestsList?: Guest[] // Opcional, se já tiver a lista de guests carregada
): Promise<AllergyConflict[]> => {
  try {
    // 1. Resolver a Receita
    let recipe: Recipe | null = null;
    if (typeof recipeOrId === 'string') {
      recipe = await getRecipeById(userId, recipeOrId);
    } else {
      recipe = recipeOrId;
    }

    if (!recipe) {
      console.warn('Receita não encontrada para verificação de alergia');
      return [];
    }

    // 2. Resolver os Convidados
    let guestsToCheck: Guest[] = [];

    if (Array.isArray(eventOrId)) {
      // Se passou lista de guests direto
      guestsToCheck = eventOrId;
    } else if (guestsList && guestsList.length > 0) {
      // Se passou lista de guests separada
      guestsToCheck = guestsList;
    } else {
      // Precisa buscar do banco ou processar ID do evento
      let guestIds: string[] = [];

      if (typeof eventOrId === 'string') {
        const event = await getEvent(eventOrId);
        if (event && event.guests) {
          guestIds = event.guests;
        }
      } else if (typeof eventOrId === 'object' && eventOrId.guests) {
        guestIds = eventOrId.guests;
      }

      if (guestIds.length > 0) {
        const allGuests = await getUserGuests(userId);
        guestsToCheck = allGuests.filter(g => g.id && guestIds.includes(g.id));
      }
    }

    const conflicts: AllergyConflict[] = [];

    // 3. Verificar conflitos
    for (const guest of guestsToCheck) {
      if (!guest.allergies || !guest.allergies.trim()) {
        continue;
      }

      // Tokens não vazios: evita que "leite," gere token "" (includes("") é sempre true → alerta falso)
      const guestAllergies = parseAllergyTokens(guest.allergies).map(a => normalizeText(a)).filter(a => a.length > 0);
      const conflictingIngredients: string[] = [];

      // Verificar cada ingrediente da receita
      if (recipe.ingredients) {
        for (const ingredient of recipe.ingredients) {
          const ingredientName = ingredient.name;
          const normalizedIngredientName = normalizeText(ingredientName);

          // 3.1. Busca por alérgenos ocultos (Expandir ingrediente)
          const hiddenAllergens = findHiddenAllergens(ingredientName);

          // 3.2. Verificar match
          for (const allergy of guestAllergies) {
            // Match direto: Ingrediente é a alergia (ex: "Amendoim" == "Amendoim")
            const DirectMatch = !!normalizedIngredientName && (normalizedIngredientName.includes(allergy) || allergy.includes(normalizedIngredientName));

            // Match oculto: Ingrediente contém alérgeno oculto que é a alergia (ex: "Paçoquinha" contém "Amendoim")
            const HiddenMatch = hiddenAllergens.some(hidden => normalizeText(hidden) === allergy);

            if (DirectMatch || HiddenMatch) {
              if (!conflictingIngredients.includes(ingredientName)) {
                conflictingIngredients.push(ingredientName);
              }
              break; // Já achou problema neste ingrediente para esta alergia, vai para o próximo ingrediente
            }
          }
        }
      }

      // 3.3 Verificar Título da Receita (Opcional, mas seguro)
      const normalizedTitle = normalizeText(recipe.title);
      for (const allergy of guestAllergies) {
        if (normalizedTitle.includes(allergy)) {
          if (!conflictingIngredients.includes(`(No título: ${recipe.title})`)) {
            conflictingIngredients.push(`(No título: ${recipe.title})`);
          }
        }
      }

      // Se houver conflitos, adiciona à lista
      if (conflictingIngredients.length > 0) {
        conflicts.push({
          guestId: guest.id || 'temp-id',
          guestName: `${guest.name} ${guest.lastName || ''}`.trim(),
          allergens: parseAllergyTokens(guest.allergies),
          conflictingIngredients,
        });
      }
    }

    return conflicts;
  } catch (error) {
    console.error('Erro ao verificar alergias:', error);
    return []; // Retorna vazio em caso de erro para não travar a UI
  }
};

/**
 * Cria notificação de aviso de alergia
 */
export const createAllergyWarning = async (
  userId: string,
  eventId: string,
  eventName: string,
  recipeName: string,
  conflicts: AllergyConflict[]
): Promise<void> => {
  try {
    const guestList = conflicts
      .map((c) => `${c.guestName} (${c.allergens.join(', ')})`)
      .join('; ');

    await createNotification({
      userId,
      type: 'allergy_warning',
      title: 'Atenção: Possíveis Alergias Detectadas',
      message: `A receita "${recipeName}" contém ingredientes que podem causar alergias em alguns convidados do evento "${eventName}": ${guestList}`,
      metadata: {
        eventId,
        conflicts,
        recipeName,
      },
    });
  } catch (error) {
    console.error('Erro ao criar notificação de alergia:', error);
    throw error;
  }
};

/**
 * Verifica alergias e retorna mensagem formatada para dialog
 */
export const getAllergyWarningMessage = (conflicts: AllergyConflict[]): string => {
  if (conflicts.length === 0) {
    return '';
  }

  let message = 'Atenção! Os seguintes convidados têm alergias que podem ser afetadas por esta receita:\n\n';

  conflicts.forEach((conflict) => {
    message += `• ${conflict.guestName}\n`;
    message += `  Alergia(s): ${conflict.allergens.join(', ')}\n`;
    message += `  Ingrediente(s) problemático(s): ${conflict.conflictingIngredients.join(', ')}\n\n`;
  });

  message += 'Tem certeza que deseja adicionar esta receita ao evento?';

  return message;
};
